import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import pl from 'date-fns/locale/pl';
import fr from 'date-fns/locale/fr';
import enUS from 'date-fns/locale/en-US';

// BackButton component
const BackButton = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  return (
    <button
      onClick={() => navigate('/mecz/turniej-rankingowy')}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
      title={getTranslation('back', language)}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = () => {
  const { language } = useLanguage();
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'turniej-rankingowy') {
          displayText = getTranslation('turniej-rankingowy', language);
        } else if (seg === 'przeglad') {
          displayText = getTranslation('przeglad-turniejow', language);
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else if (!isNaN(seg)) {
          // If it's a number (tournament ID), show the tournament ID
          displayText = seg;
        } else {
          displayText = getTranslation(seg, language);
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const TournamentReview = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [tournaments, setTournaments] = useState([]);
  const [filteredTournaments, setFilteredTournaments] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    city: '',
    tournamentname: '',
    player2: '',
    player3: '',
    player4: '',
    playersFrom: '',
    playersTo: '',
    dateFrom: '',
    dateTo: '',
    tournamentType: '' // 'all', 'single', 'dublety', 'triplety'
  });

  // Helper function to determine which player fields should be editable based on tournament type
  const getPlayerFieldsConfig = (numberofplayers) => {
    switch (numberofplayers) {
      case 1: // Single
        return {
          showPlayer2: false,
          showPlayer3: false,
          showPlayer4: false,
          player4Label: 'Rezerwowy'
        };
      case 2: // Dublety
        return {
          showPlayer2: true,
          showPlayer3: false,
          showPlayer4: false,
          player4Label: 'Rezerwowy'
        };
      case 3: // Triplety
        return {
          showPlayer2: true,
          showPlayer3: true,
          showPlayer4: false,
          player4Label: 'Rezerwowy'
        };
      case 4: // Triplety z rezerwowym
        return {
          showPlayer2: true,
          showPlayer3: true,
          showPlayer4: true,
          player4Label: 'Rezerwowy'
        };
      default:
        return {
          showPlayer2: false,
          showPlayer3: false,
          showPlayer4: false,
          player4Label: 'Rezerwowy'
        };
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);
  const [editingTournamentId, setEditingTournamentId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    tournamentname: '',
    city: '',
    place: '',
    totalplayers: '',
    player2: '',
    player3: '',
    player4: ''
  });

  // Fetch finished tournaments
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
          throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
        }
        
        const response = await fetch('/api/tournaments/finished', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Błąd ${response.status}: ${await response.text()}`);
        }
        
        const data = await response.json();
        setTournaments(data);
        setFilteredTournaments(data);
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`Błąd połączenia: ${err.message}`);
        setLoading(false);
      }
    };

    fetchTournaments();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = tournaments.filter(tournament => {
      // Parse tournament date and normalize to date only
      const tournamentDate = new Date(tournament.tournamentdate);
      const tournamentDateOnly = new Date(tournamentDate.getFullYear(), tournamentDate.getMonth(), tournamentDate.getDate());
      
      // Parse filter dates and normalize to date only
      const dateFrom = filters.dateFrom ? new Date(filters.dateFrom + 'T00:00:00') : null;
      const dateTo = filters.dateTo ? new Date(filters.dateTo + 'T23:59:59') : null;
      
      // Tournament type filter
      let typeMatch = true;
      if (filters.tournamentType === 'single') {
        typeMatch = tournament.numberofplayers === 1;
      } else if (filters.tournamentType === 'dublety') {
        typeMatch = tournament.numberofplayers === 2;
      } else if (filters.tournamentType === 'triplety') {
        typeMatch = tournament.numberofplayers === 3 || tournament.numberofplayers === 4;
      }
      
      return (
        (!filters.city || (tournament.city && tournament.city.toLowerCase().includes(filters.city.toLowerCase()))) &&
        (!filters.tournamentname || (tournament.tournamentname && tournament.tournamentname.toLowerCase().includes(filters.tournamentname.toLowerCase()))) &&
        (!filters.player2 || (tournament.player2 && tournament.player2.toLowerCase().includes(filters.player2.toLowerCase()))) &&
        (!filters.player3 || (tournament.player3 && tournament.player3.toLowerCase().includes(filters.player3.toLowerCase()))) &&
        (!filters.player4 || (tournament.player4 && tournament.player4.toLowerCase().includes(filters.player4.toLowerCase()))) &&
        (!filters.playersFrom || tournament.totalplayers >= parseInt(filters.playersFrom)) &&
        (!filters.playersTo || tournament.totalplayers <= parseInt(filters.playersTo)) &&
        (!dateFrom || tournamentDateOnly >= dateFrom) &&
        (!dateTo || tournamentDateOnly <= dateTo) &&
        typeMatch
      );
    });
    
    setFilteredTournaments(filtered);
  }, [tournaments, filters]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getTournamentType = (numberofplayers) => {
    switch (numberofplayers) {
      case 1: return getTranslation('single', language);
      case 2: return getTranslation('dublety', language);
      case 3: return getTranslation('triplety', language);
      case 4: return getTranslation('triplety-z-rezerwowym', language);
      default: return getTranslation('nieznany', language);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const handleDeleteClick = (tournament) => {
    setTournamentToDelete(tournament);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tournamentToDelete) return;
    
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
      }
      
      const response = await fetch(`/api/tournaments/${tournamentToDelete.tournamentid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Błąd ${response.status}: ${await response.text()}`);
      }
      
      // Refresh tournaments list
      const tournamentsResponse = await fetch('/api/tournaments/finished', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (tournamentsResponse.ok) {
        const tournamentsData = await tournamentsResponse.json();
        setTournaments(tournamentsData);
      }
      
      setShowDeleteModal(false);
      setTournamentToDelete(null);
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(`Błąd podczas usuwania turnieju: ${err.message}`);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTournamentToDelete(null);
  };

  const handleEditClick = (tournament) => {
    setEditingTournamentId(tournament.tournamentid);
    setEditFormData({
      tournamentname: tournament.tournamentname || '',
      city: tournament.city || '',
      place: tournament.place || '',
      totalplayers: tournament.totalplayers || '',
      player2: tournament.player2 || '',
      player3: tournament.player3 || '',
      player4: tournament.player4 || ''
    });
    setEditing(false);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditingTournamentId(null);
    setEditFormData({
      tournamentname: '',
      city: '',
      place: '',
      totalplayers: '',
      player2: '',
      player3: '',
      player4: ''
    });
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSubmit = async () => {
    if (!editingTournamentId) return;
    
    // Validation
    if (!editFormData.tournamentname.trim()) {
      setError(getTranslation('nazwa-turnieju-wymagana', language));
      return;
    }
    
    if (!editFormData.city.trim()) {
      setError(getTranslation('miasto-wymagane', language));
      return;
    }
    
    setEditing(true);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('access_token');
      
      // Get the current tournament to determine which fields should be updated
      const currentTournament = tournaments.find(t => t.tournamentid === editingTournamentId);
      const playerConfig = getPlayerFieldsConfig(currentTournament.numberofplayers);
      
      const updateData = {
        tournamentname: editFormData.tournamentname.trim(),
        city: editFormData.city.trim(),
        place: editFormData.place ? parseInt(editFormData.place) : null,
        totalplayers: editFormData.totalplayers ? parseInt(editFormData.totalplayers) : null
      };
      
      // Only include player fields that should be editable
      if (playerConfig.showPlayer2) {
        updateData.player2 = editFormData.player2.trim() || null;
      }
      if (playerConfig.showPlayer3) {
        updateData.player3 = editFormData.player3.trim() || null;
      }
      if (playerConfig.showPlayer4) {
        updateData.player4 = editFormData.player4.trim() || null;
      }
      
      const response = await fetch(`/api/tournaments/${editingTournamentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Błąd ${response.status}: ${errorText}`);
      }
      
      // Update the tournament in the local state
      setTournaments(prevTournaments => 
        prevTournaments.map(tournament => 
          tournament.tournamentid === editingTournamentId 
            ? { ...tournament, ...updateData }
            : tournament
        )
      );
      
      // Exit edit mode and reset state
      setEditingTournamentId(null);
      
    } catch (err) {
      console.error('Edit error:', err);
      setError(`Błąd podczas edytowania turnieju: ${err.message}`);
    } finally {
      setEditing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Ładowanie danych...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h2 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.5rem'
            }}>
              Błąd ładowania turniejów
            </h2>
            <p style={{ color: 'red', fontFamily: 'Nunito, Arial, sans-serif', marginBottom: '20px' }}>
              {error}
            </p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '14px'
              }}
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Language Selector */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>

      {/* Tournament List View */}
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <h2 style={{
            color: '#1e3a8a',
            fontFamily: 'Nunito, Arial, sans-serif',
            marginBottom: '20px',
            fontSize: '1.5rem',
            textAlign: 'center'
          }}>
            {getTranslation('zakonczone-turnieje', language)}
          </h2>
          
          {/* Filters */}
          <div style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#1e3a8a', fontFamily: 'Nunito, Arial, sans-serif' }}>
              {getTranslation('filtry', language)}
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '15px' 
            }}>
              <input
                type="text"
                placeholder={getTranslation('miasto', language)}
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Nunito, Arial, sans-serif'
                }}
              />
              <input
                type="text"
                placeholder={getTranslation('nazwa-turnieju', language)}
                value={filters.tournamentname}
                onChange={(e) => handleFilterChange('tournamentname', e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Nunito, Arial, sans-serif'
                }}
              />
              <input
                type="text"
                placeholder={getTranslation('gracz2', language)}
                value={filters.player2}
                onChange={(e) => handleFilterChange('player2', e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Nunito, Arial, sans-serif'
                }}
              />
              <input
                type="text"
                placeholder={getTranslation('gracz3', language)}
                value={filters.player3}
                onChange={(e) => handleFilterChange('player3', e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Nunito, Arial, sans-serif'
                }}
              />
              <input
                type="text"
                placeholder={getTranslation('rezerwowy', language)}
                value={filters.player4}
                onChange={(e) => handleFilterChange('player4', e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Nunito, Arial, sans-serif'
                }}
              />
              <input
                type="number"
                placeholder={getTranslation('liczba-graczy-od', language)}
                value={filters.playersFrom}
                onChange={(e) => handleFilterChange('playersFrom', e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Nunito, Arial, sans-serif'
                }}
              />
              <input
                type="number"
                placeholder={getTranslation('liczba-graczy-do', language)}
                value={filters.playersTo}
                onChange={(e) => handleFilterChange('playersTo', e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Nunito, Arial, sans-serif'
                }}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'pl' ? pl : language === 'fr' ? fr : enUS}>
                <DatePicker
                  label={getTranslation('data-od', language)}
                  value={filters.dateFrom ? new Date(filters.dateFrom) : null}
                  onChange={(date) => {
                    const dateString = date ? date.toISOString().split('T')[0] : '';
                    handleFilterChange('dateFrom', dateString);
                  }}
                  format={language === 'pl' ? 'dd/MM/yyyy' : language === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'}
                  slotProps={{
                    textField: {
                      placeholder: language === 'pl' ? 'dd/mm/rrrr' : language === 'fr' ? 'jj/mm/aaaa' : 'mm/dd/yyyy',
                      style: {
                        fontFamily: 'Nunito, Arial, sans-serif'
                      }
                    }
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'pl' ? pl : language === 'fr' ? fr : enUS}>
                <DatePicker
                  label={getTranslation('data-do', language)}
                  value={filters.dateTo ? new Date(filters.dateTo) : null}
                  onChange={(date) => {
                    const dateString = date ? date.toISOString().split('T')[0] : '';
                    handleFilterChange('dateTo', dateString);
                  }}
                  format={language === 'pl' ? 'dd/MM/yyyy' : language === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'}
                  slotProps={{
                    textField: {
                      placeholder: language === 'pl' ? 'dd/mm/rrrr' : language === 'fr' ? 'jj/mm/aaaa' : 'mm/dd/yyyy',
                      style: {
                        fontFamily: 'Nunito, Arial, sans-serif'
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
            
            {/* Tournament Type Radio Buttons */}
            <div style={{ 
              marginTop: '20px',
              padding: '15px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <h4 style={{ 
                margin: '0 0 15px 0', 
                color: '#1e3a8a', 
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '16px'
              }}>
                {getTranslation('typ-turnieju', language)}
              </h4>
              <div style={{ 
                display: 'flex', 
                gap: '20px',
                flexWrap: 'wrap'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px'
                }}>
                  <input
                    type="radio"
                    name="tournamentType"
                    value=""
                    checked={filters.tournamentType === ''}
                    onChange={(e) => handleFilterChange('tournamentType', e.target.value)}
                    style={{ margin: 0 }}
                  />
                  {getTranslation('wszystkie', language)}
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px'
                }}>
                  <input
                    type="radio"
                    name="tournamentType"
                    value="single"
                    checked={filters.tournamentType === 'single'}
                    onChange={(e) => handleFilterChange('tournamentType', e.target.value)}
                    style={{ margin: 0 }}
                  />
                  {getTranslation('single', language)}
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px'
                }}>
                  <input
                    type="radio"
                    name="tournamentType"
                    value="dublety"
                    checked={filters.tournamentType === 'dublety'}
                    onChange={(e) => handleFilterChange('tournamentType', e.target.value)}
                    style={{ margin: 0 }}
                  />
                  {getTranslation('dublety', language)}
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px'
                }}>
                  <input
                    type="radio"
                    name="tournamentType"
                    value="triplety"
                    checked={filters.tournamentType === 'triplety'}
                    onChange={(e) => handleFilterChange('tournamentType', e.target.value)}
                    style={{ margin: 0 }}
                  />
                  {getTranslation('triplety', language)}
                </label>
              </div>
            </div>
          </div>
          
          {filteredTournaments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ 
                fontFamily: 'Nunito, Arial, sans-serif', 
                fontSize: '1.2rem', 
                color: '#666' 
              }}>
                {tournaments.length === 0 ? getTranslation('brak-zakonczonych-turniejow', language) : getTranslation('brak-turniejow-spelniajacych-kryteria', language)}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: 'Nunito, Arial, sans-serif'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{getTranslation('szczegoly', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{getTranslation('edytuj', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{getTranslation('usun', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('data', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('miasto', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('nazwa-turnieju', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('typ', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('ilosc-graczy', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('miejsce', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('gracz2', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('gracz3', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('rezerwowy', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTournaments.map((tournament) => {
                    const isEditing = editingTournamentId === tournament.tournamentid;
                    
                    let rowStyle = { borderBottom: '1px solid #dee2e6' };
                    
                    if (isEditing) {
                      rowStyle.backgroundColor = '#fef3c7';
                      rowStyle.borderLeft = '4px solid #f59e0b';
                    }
                    
                    return (
                      <tr key={tournament.tournamentid} style={rowStyle}>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => navigate(`/mecz/turniej-rankingowy/przeglad/${tournament.tournamentid}/rundy`)}
                            disabled={isEditing}
                          style={{
                              background: isEditing ? '#9ca3af' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                              cursor: isEditing ? 'not-allowed' : 'pointer',
                            fontFamily: 'Nunito, Arial, sans-serif',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: 'background 0.2s'
                          }}
                            onMouseOver={(e) => !isEditing && (e.target.style.background = '#2563eb')}
                            onMouseOut={(e) => !isEditing && (e.target.style.background = '#3b82f6')}
                        >
                          {getTranslation('szczegoly', language)}
                        </button>
                      </td>
                        
                        {/* Edit Button */}
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditSubmit();
                                }}
                                disabled={editing}
                                style={{
                                  background: editing ? '#9ca3af' : '#22c55e',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: editing ? 'not-allowed' : 'pointer',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                {editing ? '...' : '✓'}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditCancel();
                                }}
                                disabled={editing}
                                style={{
                                  background: editing ? '#9ca3af' : '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: editing ? 'not-allowed' : 'pointer',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                        <button
                              onClick={() => handleEditClick(tournament)}
                          style={{
                                background: '#7c3aed',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontFamily: 'Nunito, Arial, sans-serif',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: 'background 0.2s'
                          }}
                              onMouseOver={(e) => e.target.style.background = '#6d28d9'}
                              onMouseOut={(e) => e.target.style.background = '#7c3aed'}
                            >
                              {getTranslation('edytuj', language)}
                            </button>
                          )}
                        </td>
                        
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteClick(tournament)}
                            disabled={isEditing}
                            style={{
                              background: isEditing ? '#9ca3af' : '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: isEditing ? 'not-allowed' : 'pointer',
                              fontFamily: 'Nunito, Arial, sans-serif',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'background 0.2s'
                            }}
                            onMouseOver={(e) => !isEditing && (e.target.style.background = '#dc2626')}
                            onMouseOut={(e) => !isEditing && (e.target.style.background = '#ef4444')}
                        >
                          {getTranslation('usun', language)}
                        </button>
                      </td>
                        
                        {/* Date - Read Only */}
                      <td style={{ padding: '12px' }}>{formatDate(tournament.tournamentdate)}</td>
                        
                        {/* City */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editFormData.city}
                              onChange={(e) => handleEditInputChange('city', e.target.value)}
                              maxLength={50}
                              style={{
                                padding: '6px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                width: '100%'
                              }}
                            />
                          ) : (
                            tournament.city || '-'
                          )}
                        </td>
                        
                        {/* Tournament Name */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editFormData.tournamentname}
                              onChange={(e) => handleEditInputChange('tournamentname', e.target.value)}
                              maxLength={100}
                              style={{
                                padding: '6px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                width: '100%'
                              }}
                            />
                          ) : (
                            tournament.tournamentname || '-'
                          )}
                        </td>
                        
                        {/* Tournament Type - Read Only */}
                      <td style={{ padding: '12px' }}>{getTournamentType(tournament.numberofplayers)}</td>
                        
                        {/* Total Players */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editFormData.totalplayers}
                              onChange={(e) => handleEditInputChange('totalplayers', e.target.value)}
                              min="1"
                              max="100"
                              style={{
                                padding: '6px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                width: '80px'
                              }}
                            />
                          ) : (
                            tournament.totalplayers || '-'
                          )}
                        </td>
                        
                        {/* Place */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editFormData.place}
                              onChange={(e) => handleEditInputChange('place', e.target.value)}
                              min="1"
                              max="100"
                              style={{
                                padding: '6px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                width: '80px'
                              }}
                            />
                          ) : (
                            tournament.place || '-'
                          )}
                        </td>
                        
                        {/* Player 2 */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            getPlayerFieldsConfig(tournament.numberofplayers).showPlayer2 ? (
                              <input
                                type="text"
                                value={editFormData.player2}
                                onChange={(e) => handleEditInputChange('player2', e.target.value)}
                                maxLength={50}
                                style={{
                                  padding: '6px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '12px',
                                  width: '100%'
                                }}
                              />
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                            )
                          ) : (
                            tournament.player2 || '-'
                          )}
                        </td>
                        
                        {/* Player 3 */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            getPlayerFieldsConfig(tournament.numberofplayers).showPlayer3 ? (
                              <input
                                type="text"
                                value={editFormData.player3}
                                onChange={(e) => handleEditInputChange('player3', e.target.value)}
                                maxLength={50}
                                style={{
                                  padding: '6px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '12px',
                                  width: '100%'
                                }}
                              />
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                            )
                          ) : (
                            tournament.player3 || '-'
                          )}
                        </td>
                        
                        {/* Player 4 (Rezerwowy) */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            getPlayerFieldsConfig(tournament.numberofplayers).showPlayer4 ? (
                              <input
                                type="text"
                                value={editFormData.player4}
                                onChange={(e) => handleEditInputChange('player4', e.target.value)}
                                maxLength={50}
                                style={{
                                  padding: '6px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '12px',
                                  width: '100%'
                                }}
                              />
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                            )
                          ) : (
                            tournament.player4 || '-'
                          )}
                        </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#fed7d7',
          color: '#c53030',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '1px solid #feb2b2',
          fontFamily: 'Nunito, Arial, sans-serif',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          maxWidth: '400px'
        }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#c53030',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginLeft: '10px',
              padding: '0'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.3rem'
            }}>
              Potwierdzenie usunięcia
            </h3>
            <p style={{
              color: '#374151',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '30px',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              Za chwilę trwale usuniesz dane turnieju i wszystkie powiązane mecze. Jesteś pewien?
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleDeleteCancel}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Nie
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Tak
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentReview;
